import { useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

interface RegistryAgent {
  name: string;
  endpoint: string;
  description?: string;
  version?: string;
}

/**
 * UnregisteredAgentPickerComponent
 *
 * Fetches agents from the Agent Gateway registry and compares them against
 * Components tagged "agent" in the Backstage catalog. Only agents that exist
 * in the registry but are NOT yet registered in the catalog are shown.
 *
 * Falls back to a free-form text input if either API call fails.
 */
const UnregisteredAgentPickerComponent = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [agents, setAgents] = useState<RegistryAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const proxyBase = await discoveryApi.getBaseUrl('proxy');
        const catalogBase = await discoveryApi.getBaseUrl('catalog');

        // Fetch all agents from the agent gateway registry
        const registryRes = await fetchApi.fetch(
          `${proxyBase}/agent-gateway/agents/discover`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: '', limit: 100 }),
          },
        );
        if (!registryRes.ok) throw new Error(`Registry API: HTTP ${registryRes.status}`);
        const registryJson = await registryRes.json();
        const registryData: any[] = registryJson.results ?? [];

        // Fetch all agent Components from the Backstage catalog
        const catalogRes = await fetchApi.fetch(
          `${catalogBase}/entities?filter=kind=Component,spec.type=service,metadata.tags=agent`,
        );
        if (!catalogRes.ok) throw new Error(`Catalog API: HTTP ${catalogRes.status}`);
        const catalogData: any[] = await catalogRes.json();

        if (!cancelled) {
          const catalogNames = new Set(
            catalogData.map((e: any) => e.metadata?.name).filter(Boolean),
          );

          const unregistered = registryData
            .filter((a: any) => !catalogNames.has(a.name))
            .map((a: any) => ({
              name: a.name ?? '',
              endpoint: a.endpoint ?? '',
              description: a.description ?? '',
              version: a.version ?? '',
            }))
            .filter(a => a.name && a.endpoint);

          setAgents(unregistered);
        }
      } catch (e: any) {
        if (!cancelled) setFetchError(String(e?.message ?? e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <CircularProgress size={24} />;
  }

  // Graceful fallback: free-form text input when agents can't be fetched
  if (fetchError || agents.length === 0) {
    return (
      <TextField
        label="Agent Endpoint URL"
        required={required}
        value={formData ?? ''}
        onChange={e => onChange(e.target.value)}
        error={(rawErrors?.length ?? 0) > 0}
        helperText={
          fetchError
            ? `Could not load agents (${fetchError}) — enter endpoint manually`
            : 'All registry agents are already in the catalog, or none found — enter endpoint manually'
        }
        fullWidth
      />
    );
  }

  return (
    <FormControl fullWidth required={required} error={(rawErrors?.length ?? 0) > 0}>
      <InputLabel>Agent (not yet in catalog)</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(String(e.target.value))}
        renderValue={val => {
          const match = agents.find(a => a.endpoint === val);
          return match ? match.name : String(val);
        }}
      >
        {agents.map(agent => (
          <MenuItem key={agent.name} value={agent.endpoint}>
            <div>
              <Typography variant="body1">{agent.name}</Typography>
              <Typography variant="caption" color="textSecondary">
                {agent.endpoint}
                {agent.description ? ` — ${agent.description}` : ''}
              </Typography>
            </div>
          </MenuItem>
        ))}
      </Select>
      {(rawErrors?.length ?? 0) > 0 && (
        <FormHelperText>{rawErrors![0]}</FormHelperText>
      )}
    </FormControl>
  );
};

export const UnregisteredAgentPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'UnregisteredAgentPicker',
    component: UnregisteredAgentPickerComponent,
  }),
);
